/**
 * Página de gestión de delegaciones físicas (Admin)
 * @module pages/CRMDelegations
 */

import { useState } from 'react';
import { PlusIcon, MapPinIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useDelegations } from '@/hooks/useDelegations';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/common/Modal';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import type { Delegation, DelegationCreate, DelegationUpdate } from '@/types/delegation.types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useForm } from 'react-hook-form';

type DelegationFormData = DelegationCreate | DelegationUpdate;

export function CRMDelegations() {
  const { delegations, isLoading, create, update, delete: deleteDelegation } = useDelegations();
  const [showModal, setShowModal] = useState(false);
  const [editingDelegation, setEditingDelegation] = useState<Delegation | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<DelegationFormData>();

  const handleCreate = () => {
    reset({
      name: '',
      address: '',
      city: '',
      postal_code: '',
      country: 'España',
    });
    setEditingDelegation(null);
    setShowModal(true);
  };

  const handleEdit = (delegation: Delegation) => {
    reset(delegation);
    setEditingDelegation(delegation);
    setShowModal(true);
  };

  const handleDelete = async (delegationId: string) => {
    if (confirm('¿Estás seguro de eliminar esta delegación?')) {
      try {
        await deleteDelegation(delegationId);
      } catch (error) {
        console.error('Error eliminando delegación:', error);
      }
    }
  };

  const onSubmit = async (data: DelegationFormData) => {
    try {
      if (editingDelegation) {
        await update({ id: editingDelegation.id, data });
      } else {
        await create(data as DelegationCreate);
      }
      setShowModal(false);
      reset();
    } catch (error) {
      console.error('Error guardando delegación:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Delegaciones</h1>
            <p className="text-sm text-gray-600 mt-1">
              Gestiona las oficinas físicas de Migro
            </p>
          </div>
          <Button onClick={handleCreate}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Nueva Delegación
          </Button>
        </div>
      </div>

      {/* Lista de delegaciones */}
      {!delegations || delegations.length === 0 ? (
        <EmptyState
          icon={<MapPinIcon className="w-12 h-12" />}
          title="No hay delegaciones"
          description="Comienza agregando una nueva delegación."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {delegations.map((delegation) => (
            <Card key={delegation.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <MapPinIcon className="w-5 h-5 text-blue-500" />
                    <CardTitle className="text-lg">{delegation.name}</CardTitle>
                  </div>
                  <Badge variant={delegation.is_active ? 'default' : 'secondary'}>
                    {delegation.is_active ? 'Activa' : 'Inactiva'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>{delegation.address}</p>
                  <p>
                    {delegation.postal_code} {delegation.city}
                  </p>
                  <p>{delegation.country}</p>
                </div>
                <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                  <Button
                    onClick={() => handleEdit(delegation)}
                    variant="outline"
                    size="sm"
                    className="flex-1"
                  >
                    <PencilIcon className="w-4 h-4 mr-1" />
                    Editar
                  </Button>
                  <Button
                    onClick={() => handleDelete(delegation.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de crear/editar */}
      {showModal && (
        <Modal
          open={showModal}
          onClose={() => {
            setShowModal(false);
            reset();
          }}
          title={editingDelegation ? 'Editar Delegación' : 'Nueva Delegación'}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                {...register('name', { required: 'Nombre requerido' })}
                placeholder="Ej: Madrid"
              />
              {errors.name && (
                <p className="text-sm text-red-600 mt-1">{errors.name.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="address">Dirección *</Label>
              <Input
                id="address"
                {...register('address', { required: 'Dirección requerida' })}
                placeholder="Ej: Calle Ejemplo 123"
              />
              {errors.address && (
                <p className="text-sm text-red-600 mt-1">{errors.address.message}</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city">Ciudad *</Label>
                <Input
                  id="city"
                  {...register('city', { required: 'Ciudad requerida' })}
                  placeholder="Ej: Madrid"
                />
                {errors.city && (
                  <p className="text-sm text-red-600 mt-1">{errors.city.message}</p>
                )}
              </div>

              <div>
                <Label htmlFor="postal_code">Código Postal *</Label>
                <Input
                  id="postal_code"
                  {...register('postal_code', { required: 'CP requerido' })}
                  placeholder="Ej: 28001"
                />
                {errors.postal_code && (
                  <p className="text-sm text-red-600 mt-1">{errors.postal_code.message}</p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="country">País *</Label>
              <Input
                id="country"
                {...register('country', { required: 'País requerido' })}
                placeholder="Ej: España"
              />
              {errors.country && (
                <p className="text-sm text-red-600 mt-1">{errors.country.message}</p>
              )}
            </div>

            <div className="flex items-center gap-3 pt-4">
              <Button type="submit" disabled={isSubmitting} className="flex-1">
                {isSubmitting ? 'Guardando...' : 'Guardar'}
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setShowModal(false);
                  reset();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
